import { useEffect, useState, useRef, useCallback } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import ThShaper from './shaper.js';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import {background} from "@vayu-blocks/global";
import classnames from 'classnames';

const ResizableContainer = ({
    attributes,
    setAttributes,
    clientId,
    containerBlockProps,
    innerBlocksProps,
    showShouldOverlay,
    parentBlock,
    hasInnerBlocks,
    rootContainerClassName,
    getBlockParents,
}) => {
    const deviceView = VayuBlocksDevice();

    const isDesktop = deviceView === 'Desktop';
    const isTablet = deviceView === 'Tablet';
    const isMobile = deviceView === 'Mobile';

    const containerRef = useRef(null);
    const draggerRef = useRef(null);
    const isResizingRef = useRef(false);
    const animationFrameRef = useRef(null);

    const getWidth = () => {
        if (isMobile) return attributes.fullcontentWidthMobile || 100;
        if (isTablet) return attributes.fullcontentWidthTablet || 100;
        return attributes.fullcontentWidth || 100;
    };

    const getUnit = () => {
        if (isMobile) return attributes.fullcontentWidthUnitMobile || '%';
        if (isTablet) return attributes.fullcontentWidthUnitTablet || '%';
        return attributes.fullcontentWidthUnit || '%';
    };

    const widthRef = useRef(getWidth());
    const [width, setWidth] = useState(widthRef.current);
    const [unit, setUnit] = useState(getUnit());
    const [dragging, setDragging] = useState(false);
    const [resized, setResized] = useState(false);

    useEffect(() => {
        widthRef.current = getWidth();
        setWidth(widthRef.current);
        setUnit(getUnit());
    }, [attributes.fullcontentWidth, deviceView]);

    // const resize = useCallback((e) => {
    //     if (!isResizingRef.current || !containerRef.current?.parentElement) return;
    //     if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    //     animationFrameRef.current = requestAnimationFrame(() => {
    //         const parentWidth = containerRef.current.parentElement.clientWidth;
    //         let newWidth;

    //         switch (unit) {
    //             case '%':
    //                 newWidth = Math.min(Math.max(((e.clientX - containerRef.current.offsetLeft) / parentWidth) * 100), 100);
    //                 break;
    //             case 'px':
    //                 newWidth = Math.min(Math.max(e.clientX - containerRef.current.offsetLeft, 50), parentWidth);
    //                 break;
    //             case 'em': {
    //                 const fontSize = parseFloat(getComputedStyle(containerRef.current).fontSize) || 16;
    //                 newWidth = Math.min(Math.max((e.clientX - containerRef.current.offsetLeft) / fontSize, 5), parentWidth / fontSize);
    //                 break;
    //             }
    //             default:
    //                 newWidth = widthRef.current;
    //         }

    //         newWidth = Math.round(newWidth);
    //         widthRef.current = newWidth;
    //         setWidth(newWidth);

    //         setAttributes({
    //             ...(isMobile ? { fullcontentWidthMobile: newWidth, fullcontentWidthUnitMobile: unit } :
    //                 isTablet ? { fullcontentWidthTablet: newWidth, fullcontentWidthUnitTablet: unit } :
    //                     { fullcontentWidth: newWidth, fullcontentWidthUnit: unit }),
    //             contentWidthType: 'fullwidth',
    //         });
    //     });
    // }, [unit, setAttributes, isMobile, isTablet]);

    const resize = useCallback((e) => {
        if (!isResizingRef.current || !containerRef.current?.parentElement) return;
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    
        animationFrameRef.current = requestAnimationFrame(() => {
            const parentWidth = containerRef.current.parentElement.clientWidth;
            let newWidth;
    
            switch (unit) {
                case '%':
                    newWidth = Math.min(Math.max(((e.clientX - containerRef.current.offsetLeft) / parentWidth) * 100), 100);
                    break;
                case 'px':
                    newWidth = Math.min(Math.max(e.clientX - containerRef.current.offsetLeft, 50), parentWidth);
                    break;
                case 'em': {
                    const fontSize = parseFloat(getComputedStyle(containerRef.current).fontSize) || 16;
                    newWidth = Math.min(Math.max((e.clientX - containerRef.current.offsetLeft) / fontSize, 5), parentWidth / fontSize);
                    break;
                }
                default:
                    newWidth = widthRef.current;
            }
    
            newWidth = Math.round(newWidth);
            widthRef.current = newWidth;
            setWidth(newWidth);
    
            // ✅ Define and use `updatedWidth` **inside** this callback
            const updatedWidth = {
                ...attributes.fullcontentWidth,
                [isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop']: newWidth,
            };
    
            setAttributes({
                fullcontentWidth: updatedWidth,
                contentWidthType: 'fullwidth',
            });
        });
    }, [unit, setAttributes, attributes.fullcontentWidth, isMobile, isTablet]);
    

    const stopResizing = useCallback(() => {
        isResizingRef.current = false;
        setDragging(false);

        window.removeEventListener('mousemove', resize);
        window.removeEventListener('mouseup', stopResizing);
        window.removeEventListener('touchmove', resize, { capture: true, passive: false });
        window.removeEventListener('touchend', stopResizing);

        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }, [resize]);

    const startResizing = (e) => {
        e.preventDefault();
        isResizingRef.current = true;
        setDragging(true);
        setResized(true);
        window.addEventListener('mousemove', resize, { passive: false });
        window.addEventListener('mouseup', stopResizing, { passive: false });
        window.addEventListener('touchmove', resize, { capture: true, passive: false });
        window.addEventListener('touchend', stopResizing, { passive: false });
    };

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
            window.removeEventListener('touchmove', resize);
            window.removeEventListener('touchend', stopResizing);
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [resize, stopResizing]);

    const Tag = attributes.containerHTMLTag || 'div';

    const dynamicStyle = {
        width: `${Math.round(width)}${unit}`,
        // maxWidth: '100%',
        transition: dragging ? 'none' : 'width 0.2s ease-out',
        cursor: 'pointer',
        flexGrow: '0', 
        flexShrink: '1',
        flexBasis:'auto',
        // ...(resized ? { flexGrow: '0', flexShrink: '0' } : {}),
    };

    let overlayBackground;
    let overlayBackgroundHover;
    
    overlayBackground = {
        ...background(attributes.overlaybackground),
    };
    
    overlayBackgroundHover = {
        ...background(attributes.overlaybackgroundHover),
    };
    const omitBy = (object, condition) => (
		Object.fromEntries(
			Object.entries(object).filter(([key, value]) => !condition(value))
		)
	);

    const filteredHoverStyles = omitBy( overlayBackgroundHover, value => value === undefined || value === null);

    const [isHovered, setIsHovered] = useState(false);
   // Define overlay style, switching between normal and hover states
    const overlayStyle = {
        ...overlayBackground,
        ...(isHovered ? filteredHoverStyles : {}),
        transition: 'background 0.3s ease',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    };

    // Fetch inner blocks to check for vayu-blocks/advance-container
    const hasInnerContainer = useSelect(
        (select) => {
            const { getBlock } = select('core/block-editor');
            const block = getBlock(clientId);
            return block?.innerBlocks?.some((innerBlock) => innerBlock.name === 'vayu-blocks/advance-container') || false;
        },
        [clientId]
    );

    // Determine if the container is a parent or child
    const isChildContainer = parentBlock === 'vayu-blocks/advance-container'; // Child if nested in vayu-blocks/advance-container
    const isParentContainer = !isChildContainer && hasInnerContainer; // Parent if not nested and has inner containers
    const containerClasses = classnames({
        'vayu-container-parent': isParentContainer,
        'vayu-container-child': isChildContainer,
        [containerBlockProps.className]: !!containerBlockProps.className,
    });
                
    return (
        <>
            {parentBlock === 'vayu-blocks/advance-container' || !hasInnerBlocks ? (
                <Tag
                    {...containerBlockProps}
                    className={containerClasses}
                    ref={(el) => {
                        containerRef.current = el;
                        if (containerBlockProps.ref) {
                            if (typeof containerBlockProps.ref === 'function') {
                                containerBlockProps.ref(el);
                            } else if (typeof containerBlockProps.ref === 'object') {
                                containerBlockProps.ref.current = el;
                            }
                        }
                    }}
                    style={{ ...containerBlockProps.style, ...dynamicStyle }}
                >
                    {showShouldOverlay && 
                    <div
                    className="wp-block-th-blocks-container-overlay"
                    style={overlayStyle}
                    onMouseEnter={() => setIsHovered(true)} // Enable hover state
                    onMouseLeave={() => setIsHovered(false)} // Disable hover state
                    />
                    }
                    <ThShaper attributes={attributes} />
                    <div {...innerBlocksProps}>{innerBlocksProps.children}</div>
                    <div
                        ref={draggerRef}
                        className="resizable-dragger"
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '5px',
                            cursor: 'ew-resize',
                            background: 'transparent',
                            borderRadius: '4px',
                            transition: 'background 0.2s ease',
                            zIndex:'2'
                        }}
                        onMouseDown={startResizing}
                        onTouchStart={startResizing}
                    />
                </Tag>
            ) : (
                <div className={rootContainerClassName} ref={containerRef} style={{ ...dynamicStyle }}>
                    <Tag {...containerBlockProps} className={containerClasses}>
                        {showShouldOverlay && 
                        <div
                        className="wp-block-th-blocks-container-overlay"
                        style={overlayStyle}
                        onMouseEnter={() => setIsHovered(true)} // Enable hover state
                        onMouseLeave={() => setIsHovered(false)} // Disable hover state
                        />
                        }
                        <ThShaper attributes={attributes} />
                        <div {...innerBlocksProps}>{innerBlocksProps.children}</div>
                    </Tag>
                    <div
                        ref={draggerRef}
                        className="resizable-dragger"
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '1.5px',
                            cursor: 'ew-resize',
                            background: 'transparent',
                            borderRadius: '4px',
                            transition: 'background 0.2s ease',
                            zIndex:'2'
                        }}
                        onMouseDown={startResizing}
                        onTouchStart={startResizing}
                    />
                </div>
            )}
        </>
    );
};

export default ResizableContainer;